import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { MapPin, Thermometer, Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ElementiCucina = ({ embedded = false, frigoOnly = false, headerTitle = null }) => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [posizioni, setPosizioni] = useState([]);
  const [frigoriferi, setFrigoriferi] = useState([]);
  const [tipologie, setTipologie] = useState([]);
  const [tab, setTab] = useState(frigoOnly ? "frigoriferi" : "posizioni");
  
  // Dialog state (usato solo quando NON embedded)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // Inline form state (usato solo quando embedded=true)
  const [inlineView, setInlineView] = useState(null); // null | "add-frigo" | "edit-frigo"
  
  // Form state
  const [posizioneForm, setPosizioneForm] = useState({ nome: "" });
  const [frigoForm, setFrigoForm] = useState({
    nome: "",
    tipologia: "",
    temperatura_min: "",
    temperatura_max: ""
  });

  useEffect(() => {
    if (localeCorrente) {
      fetchData();
    }
  }, [localeCorrente]);

  const fetchData = async () => {
    if (!localeCorrente) return;
    
    setLoading(true);
    try {
      const [posizioniRes, frigoriferiRes, tipologieRes] = await Promise.all([
        axios.get(`${API}/posizioni?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/frigoriferi?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/tipologie-frigorifero`, { headers: getAuthHeaders() })
      ]);
      setPosizioni(posizioniRes.data);
      setFrigoriferi(frigoriferiRes.data);
      setTipologie(tipologieRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type, item = null) => {
    setDialogType(type);
    setEditItem(item);
    
    if (type === "posizione") {
      setPosizioneForm({ nome: item?.nome || "" });
    } else {
      setFrigoForm({
        nome: item?.nome || "",
        tipologia: item?.tipologia || "",
        temperatura_min: item?.temperatura_min?.toString() || "",
        temperatura_max: item?.temperatura_max?.toString() || ""
      });
    }

    // In modalità embedded usa form inline, altrimenti apri dialog
    if (embedded && type === "frigorifero") {
      setInlineView(item ? "edit-frigo" : "add-frigo");
    } else {
      setDialogOpen(true);
    }
  };

  // Quando cambia la tipologia, auto-compila i range di temperatura
  const handleTipologiaChange = (tipologia) => {
    const tipologiaData = tipologie.find(t => t.nome === tipologia);
    setFrigoForm(prev => ({
      ...prev,
      tipologia,
      temperatura_min: tipologiaData?.temperatura_min?.toString() || prev.temperatura_min,
      temperatura_max: tipologiaData?.temperatura_max?.toString() || prev.temperatura_max
    }));
  };

  const handleSubmitPosizione = async (e) => {
    e.preventDefault();
    if (!posizioneForm.nome.trim()) {
      toast.error("Nome obbligatorio");
      return;
    }

    setSaving(true);
    try {
      if (editItem) {
        // Non c'è endpoint update per posizioni, solo delete e create
        toast.error("Modifica posizione non supportata");
      } else {
        await axios.post(`${API}/posizioni`, {
          nome: posizioneForm.nome,
          locale_id: localeCorrente.id
        }, { headers: getAuthHeaders() });
        toast.success("Posizione creata");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitFrigo = async (e) => {
    e.preventDefault();
    if (!frigoForm.nome.trim() || !frigoForm.tipologia || frigoForm.temperatura_min === "" || frigoForm.temperatura_max === "") {
      toast.error("Tutti i campi sono obbligatori");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: frigoForm.nome,
        tipologia: frigoForm.tipologia,
        temperatura_min: frigoForm.temperatura_min ? parseFloat(frigoForm.temperatura_min) : null,
        temperatura_max: frigoForm.temperatura_max ? parseFloat(frigoForm.temperatura_max) : null,
        locale_id: localeCorrente.id
      };

      if (editItem) {
        await axios.put(`${API}/frigoriferi/${editItem.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Frigorifero aggiornato");
      } else {
        await axios.post(`${API}/frigoriferi`, payload, { headers: getAuthHeaders() });
        toast.success("Frigorifero creato");
      }

      if (inlineView) {
        setInlineView(null);
        setEditItem(null);
      } else {
        setDialogOpen(false);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type, id) => {
    const confirmMsg = type === "posizione" 
      ? "Sei sicuro di voler eliminare questa posizione?" 
      : "Sei sicuro di voler eliminare questo frigorifero? Verranno eliminate anche tutte le temperature registrate.";
    
    if (!await confirm(confirmMsg)) return;

    try {
      const endpoint = type === "posizione" ? "posizioni" : "frigoriferi";
      await axios.delete(`${API}/${endpoint}/${id}`, { headers: getAuthHeaders() });
      toast.success(`${type === "posizione" ? "Posizione" : "Frigorifero"} eliminato`);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (!localeCorrente) {
    return (
      <div className="empty-state">
        <MapPin className="empty-state-icon" />
        <p className="empty-state-text">Seleziona un locale per visualizzare gli elementi</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="text-muted-custom">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "page-container"} data-testid="elementi-cucina-page">
      {!embedded && (
        <div>
          <h1 className="page-title">Postazioni & Attrezzature</h1>
          <p className="page-subtitle">
            Gestisci posizioni e frigoriferi di <span className="text-lime-400">{localeCorrente.nome}</span>
          </p>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        {!frigoOnly && (
          <TabsList className="tabs-pill-list">
            <TabsTrigger value="posizioni" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Posizioni
            </TabsTrigger>
            <TabsTrigger value="frigoriferi" className="flex items-center gap-2">
              <Thermometer className="h-4 w-4" />
              Frigoriferi
            </TabsTrigger>
          </TabsList>
        )}

        {/* Tab Posizioni */}
        <TabsContent value="posizioni" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button 
              onClick={() => openDialog("posizione")}
              className="bg-primary hover:bg-primary/90 text-white rounded-xl"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuova Posizione
            </Button>
          </div>

          <div className="space-y-3">
            {posizioni.map((pos) => (
              <div 
                key={pos.id}
                className="bg-card backdrop-blur-sm border border-border rounded-2xl p-4 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-blue-400" />
                  </div>
                  <span className="font-medium text-white truncate">{pos.nome}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => handleDelete("posizione", pos.id)}
                  className="h-10 w-10 text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0"
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </div>
            ))}
          </div>

          {posizioni.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna posizione configurata</p>
            </div>
          )}
        </TabsContent>

        {/* Tab Frigoriferi */}
        <TabsContent value="frigoriferi" className="mt-4">

          {/* ── FORM INLINE (solo embedded) ── */}
          {inlineView ? (
            <div className="space-y-4">
              {/* Header con back */}
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => { setInlineView(null); setEditItem(null); }}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                  Indietro
                </button>
                <span className="text-border">|</span>
                <h2 className="text-base font-semibold text-foreground">
                  {inlineView === "edit-frigo" ? "Modifica Frigorifero" : "Nuovo Frigorifero"}
                </h2>
              </div>

              <form onSubmit={handleSubmitFrigo} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-foreground/70">Nome *</Label>
                  <Input
                    value={frigoForm.nome}
                    onChange={(e) => setFrigoForm(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="es. Frigo Principale"
                    required
                    className="bg-card border-border text-foreground rounded-xl"
                    autoFocus
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-foreground/70">Tipologia *</Label>
                  <Select value={frigoForm.tipologia} onValueChange={handleTipologiaChange}>
                    <SelectTrigger className="bg-card border-border text-foreground rounded-xl">
                      <SelectValue placeholder="Seleziona tipologia" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {tipologie.map(t => (
                        <SelectItem key={t.nome} value={t.nome} className="text-foreground">
                          <div className="flex items-center justify-between w-full gap-4">
                            <span>{t.nome}</span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {t.temperatura_min}° / {t.temperatura_max}°C
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {frigoForm.tipologia && (
                    <p className="text-xs text-muted-foreground">
                      Range consigliato auto-compilato. Puoi modificarlo se necessario.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Temp. Min (°C) *</Label>
                    <Input
                      type="number"
                      value={frigoForm.temperatura_min}
                      onChange={(e) => setFrigoForm(prev => ({ ...prev, temperatura_min: e.target.value }))}
                      placeholder="-18"
                      className="bg-card border-border text-foreground rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground/70">Temp. Max (°C) *</Label>
                    <Input
                      type="number"
                      value={frigoForm.temperatura_max}
                      onChange={(e) => setFrigoForm(prev => ({ ...prev, temperatura_max: e.target.value }))}
                      placeholder="4"
                      className="bg-card border-border text-foreground rounded-xl"
                    />
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setInlineView(null); setEditItem(null); }}
                    className="flex-1 border-border text-foreground hover:bg-muted rounded-xl"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-xl"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? "Salvataggio..." : "Salva"}
                  </Button>
                </div>
              </form>
            </div>

          ) : (
            /* ── LISTA FRIGO ── */
            <>
              <div className="flex items-center justify-between mb-4">
                {headerTitle ? (
                  <h2 className="text-lg font-semibold text-foreground">{headerTitle}</h2>
                ) : (
                  <span />
                )}
                <Button
                  onClick={() => openDialog("frigorifero")}
                  className="bg-primary hover:bg-primary/90 text-white rounded-xl"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Nuovo Frigorifero
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {frigoriferi.map((frigo) => (
                  <div
                    key={frigo.id}
                    className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 shrink-0 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                      <Thermometer className="h-5 w-5 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground text-sm truncate">{frigo.nome}</p>
                      <p className="text-xs text-muted-foreground truncate">{frigo.tipologia}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-xs">
                          {frigo.temperatura_min}° / {frigo.temperatura_max}°C
                        </span>
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDialog("frigorifero", frigo)}
                        className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete("frigorifero", frigo.id)}
                        className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {frigoriferi.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Thermometer className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun frigorifero configurato</p>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Posizione */}
      <Dialog open={dialogOpen && dialogType === "posizione"} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-foreground">Nuova Posizione</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitPosizione} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground/70">Nome Posizione *</Label>
              <Input
                value={posizioneForm.nome}
                onChange={(e) => setPosizioneForm({ nome: e.target.value })}
                placeholder="es. Scaffale Alto, Banco Lavoro"
                required
                className="bg-card border-border text-foreground rounded-xl"
              />
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-border text-foreground hover:bg-muted"
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Salvataggio..." : "Crea"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Frigorifero */}
      <Dialog open={dialogOpen && dialogType === "frigorifero"} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editItem ? "Modifica Frigorifero" : "Nuovo Frigorifero"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitFrigo} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground/70">Nome *</Label>
              <Input
                value={frigoForm.nome}
                onChange={(e) => setFrigoForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="es. Frigo Principale"
                required
                className="bg-card border-border text-foreground rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-foreground/70">Tipologia *</Label>
              <Select 
                value={frigoForm.tipologia} 
                onValueChange={handleTipologiaChange}
              >
                <SelectTrigger className="bg-card border-border text-foreground rounded-xl">
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {tipologie.map(t => (
                    <SelectItem key={t.nome} value={t.nome} className="text-foreground">
                      <div className="flex items-center justify-between w-full gap-4">
                        <span>{t.nome}</span>
                        <span className="text-xs text-muted-foreground font-mono">
                          {t.temperatura_min}° / {t.temperatura_max}°C
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {frigoForm.tipologia && (
                <p className="text-xs text-muted-foreground">
                  Range consigliato auto-compilato. Puoi modificarlo se necessario.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground/70">Temp. Min (°C) *</Label>
                <Input
                  type="number"
                  value={frigoForm.temperatura_min}
                  onChange={(e) => setFrigoForm(prev => ({ ...prev, temperatura_min: e.target.value }))}
                  placeholder="-18"
                  className="bg-card border-border text-foreground rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/70">Temp. Max (°C) *</Label>
                <Input
                  type="number"
                  value={frigoForm.temperatura_max}
                  onChange={(e) => setFrigoForm(prev => ({ ...prev, temperatura_max: e.target.value }))}
                  placeholder="4"
                  className="bg-card border-border text-foreground rounded-xl"
                />
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-border text-foreground hover:bg-muted"
              >
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-primary hover:bg-primary/90 text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Salvataggio..." : "Salva"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ElementiCucina;
